import { useLayoutEffect, useRef, type FocusEvent, type KeyboardEvent } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { Input } from '@/UI/Input/Input';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { color } from '@/colors';

const inputSx = {
  height: 24,
  py: 0.5,
  px: 1,
  border: `1px solid ${token.border}`,
  borderRadius: '4px',
  background: 'transparent',
  color: token.textSecondary,
  fontFamily: '"DM Mono"',
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1,
  outline: 'none',
  boxSizing: 'border-box',
  '&:focus': {
    borderColor: color.White40_T,
    color: token.text,
  },
  '&::selection': {
    background: color.White16_T,
  },
};

interface EditableInputProps {
  syncedValue: string;
  ariaLabel: string;
  width: number;
  /** Omit for inputs with no playback to pause, e.g. the Trim node's Start/End fields. */
  paused?: boolean;
  onTogglePlay?: () => void;
  onCommit: (value: string) => string | null;
}

export function EditableInput({
  syncedValue,
  ariaLabel,
  width,
  paused = true,
  onTogglePlay,
  onCommit,
}: EditableInputProps) {
  const isFpl = useFplMigrationFlag();
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input && document.activeElement !== input) {
      input.value = syncedValue;
    }
  }, [syncedValue]);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
    if (!paused) onTogglePlay?.();
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    if (value === syncedValue) return;
    event.currentTarget.value = onCommit(value) ?? syncedValue;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.currentTarget.value = syncedValue;
      event.currentTarget.blur();
    }
  };

  if (isFpl) {
    return (
      <Input
        inputRef={inputRef}
        aria-label={ariaLabel}
        size="small"
        sx={{ width }}
        defaultValue={syncedValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        noBlurOnEnter
        autoComplete="off"
      />
    );
  }

  return (
    <Box
      component="input"
      aria-label={ariaLabel}
      ref={inputRef}
      sx={{ ...inputSx, width }}
      defaultValue={syncedValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      data-1p-ignore="true"
      data-lpignore="true"
      data-bwignore="true"
      data-form-type="other"
    />
  );
}
